import { mergeStyles, StylesConfig, GroupBase } from 'react-select';
import { SelectSize } from './enums';
import variables from './js-variables.scss';

const BORDER_WIDTH = '2px';

const getValueContainerPadding = (y: string, x: string): string =>
  `calc(${y} - ${BORDER_WIDTH}) calc(${x} - ${BORDER_WIDTH})`;

const defaultBaseStyles = <OptionType, IsMulti extends boolean>(): StylesConfig<OptionType, IsMulti> => ({
  container: (base) => ({
    ...base,
    fontFamily: variables.fontFamily,
  }),
  control: (base, { isFocused }) => ({
    ...base,
    borderWidth: BORDER_WIDTH,
    borderStyle: 'solid',
    borderRadius: '4px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: variables.primaryPracticalBlue,
    },
    // There is a '0 !important' coming from react-select itself, we have to use '!important' to override it
    outline: isFocused ? `4px solid ${variables.practical500} !important` : '0 !important',
    minHeight: 'unset',
    padding: 0,
  }),
  placeholder: (base, { isDisabled }) => ({
    ...base,
    color: isDisabled ? variables.iron300 : variables.iron600,
  }),
  singleValue: (base, { isDisabled }) => ({
    ...base,
    color: isDisabled ? variables.iron300 : variables.primaryPracticalBlue,
    width: '100%',
    maxWidth: 'calc(100% - 0.7em)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: variables.primaryPracticalBlue,
    '&:hover': {
      color: variables.primaryPracticalBlue,
    },
  }),
  menu: (base) => ({
    ...base,
    marginTop: '1px',
    marginBottom: '1px',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    textAlign: 'left',
  }),
  groupHeading: (base) => ({
    ...base,
    marginBottom: '0.5em',
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? variables.magic050 : 'transparent',
    color: isSelected || isFocused ? variables.primaryPracticalBlue : variables.iron600,
    fontWeight: parseInt(isSelected ? variables.fontSemibold : variables.fontRegular, 10),
    padding: '0.8em',
  }),
  input: (base) => ({
    ...base,
    margin: 'unset',
    padding: 'unset',
  }),
});

const defaultSingleStyles = <OptionType>(): StylesConfig<OptionType, false> =>
  mergeStyles(defaultBaseStyles<OptionType, false>(), {
    indicatorSeparator: () => ({
      display: 'none',
    }),
  });

const defaultMultiStyles = <OptionType>(): StylesConfig<OptionType, true, GroupBase<OptionType>> =>
  mergeStyles(defaultBaseStyles<OptionType, true>(), {
    control: (base) => ({
      ...base,
      flexWrap: 'nowrap',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: variables.iron100,
      color: variables.iron800,
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontSize: '1em',
    }),
  });

const sizeSmallStyles = <OptionType, IsMulti extends boolean>(): StylesConfig<OptionType, IsMulti> => ({
  container: (base) => ({
    ...base,
    font: variables.fontPsReg,
  }),
  valueContainer: (base, { isMulti, hasValue }) => ({
    ...base,
    padding: getValueContainerPadding(
      isMulti && hasValue ? variables.space4 : variables.space8,
      isMulti && hasValue ? variables.space8 : variables.space12
    ),
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: getValueContainerPadding(variables.space8, variables.space12),
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: getValueContainerPadding(variables.space8, variables.space12),
  }),
  input: (base) => ({
    ...base,
    font: variables.fontPsReg,
    height: `calc(${variables.fontPsSize} * ${variables.fontPsLineHeight})`,
  }),
  multiValue: (base) => ({
    ...base,
    margin: variables.space4,
  }),
  multiValueLabel: (base) => ({
    ...base,
    padding: '0 0.25em 0 0.5em',
  }),
  option: (base) => ({
    ...base,
    padding: `${variables.space8} ${variables.space12}`,
  }),
});

const sizeLargeStyles = <OptionType, IsMulti extends boolean>(): StylesConfig<OptionType, IsMulti> => ({
  container: (base) => ({
    ...base,
    font: variables.fontPReg,
  }),
  valueContainer: (base, { isMulti, hasValue }) => ({
    ...base,
    padding: getValueContainerPadding(
      isMulti && hasValue ? variables.space8 : variables.space12,
      isMulti && hasValue ? variables.space12 : variables.space16
    ),
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: getValueContainerPadding(variables.space12, variables.space16),
  }),
  multiValue: (base) => ({
    ...base,
    margin: variables.space4,
  }),
  multiValueLabel: (base) => ({
    ...base,
    padding: '0 0.3em 0 0.6em',
  }),
  option: (base) => ({
    ...base,
    padding: `${variables.space12} ${variables.space16}`,
  }),
});

const getSizeStyles = <Option, IsMulti extends boolean>(size: SelectSize) =>
  size === SelectSize.Small ? sizeSmallStyles<Option, IsMulti>() : sizeLargeStyles<Option, IsMulti>();

export const getDefaultSingleStyles = <Option>(size: SelectSize): StylesConfig<Option, false> =>
  mergeStyles<Option, false, GroupBase<Option>>(defaultSingleStyles(), getSizeStyles(size));

export const getDefaultMultiStyles = <Option>(size: SelectSize): StylesConfig<Option, true> =>
  mergeStyles<Option, true, GroupBase<Option>>(defaultMultiStyles(), getSizeStyles(size));
